import { LabelOutlined, Person } from '@mui/icons-material';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { EditableCard, InfoItem } from 'src/careflow/common/EditableCard';
import { ItemBox } from '../pages/EpisodeOfCareDetails';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { EpisodeOfCareWithCareManager } from '../../patient/episodeOfCare/EpisodeOfCareList';
import { shortFormat } from 'src/utils/formatTime';
import { CRS_PATH } from 'src/routes/paths';
import { IdentifierDetails } from '../../patient/identifier-detail/IdentifierDetails';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import produce from 'immer';
import CareTeam from './CareTeam';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { useMemo } from 'react';

export default function General({
  eoc,
  handle,
}: {
  eoc: EpisodeOfCareWithCareManager;
  handle: Function;
}) {
  const { i18n } = useLocales();

  const { includedResources, ...resource } = eoc ?? {};
  const { subject, conditions, careManager, organization } = includedResources ?? {};

  const {
    valueSets: [eocIdentifierTypesVS, eocStatusVS],
  } = useValueSetsByIdentifiers(['ph-eoc-identifier-types', 'episode-of-care-status'], {
    autofetch: !!eoc,
  });

  const eocStatus = useMemo(
    () => getValueSetConceptValue(eocStatusVS?.asListAll() ?? [], resource?.status),
    [resource, eocStatusVS]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={9}>
        <EditableCard
          title={i18n('episodeOfCare.details.general.episodeOverview', 'crs')}
          showEditButton={false}
        >
          <Stack direction="row" mt={1}>
            <Grid item xs={4}>
              <InfoItem
                label={i18n('episodeOfCare.details.general.type', 'crs')}
                value={resource?.getFirstType?.()?.display}
              />
            </Grid>
            <Grid item xs={3}>
              <InfoItem
                label={i18n('episodeOfCare.details.general.period', 'crs')}
                value={resource?.getPeriod?.()}
              />
            </Grid>
            <Grid item xs={2}>
              <InfoItem
                label={i18n('episodeOfCare.details.general.status', 'crs')}
                value={eocStatus?.display ?? eocStatus?.code ?? ''}
              />
            </Grid>
            <Grid item xs={3}>
              <InfoItem
                label={i18n('episodeOfCare.details.general.organization', 'crs')}
                value={organization?.name}
              />
            </Grid>
          </Stack>
          <Grid item xs={12} mt={2}>
            <InfoItem label={i18n('episodeOfCare.details.general.diagnosis', 'crs')} value="" />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 1,
                p: 2,
                bgcolor: (theme) => theme.palette.primary.main + '10',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: (theme) => theme.palette.primary.main + '30',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LabelOutlined color="primary" fontSize="small" />
              </Box>

              <Box>
                <Typography fontWeight="bold">
                  {conditions?.[0]?.getConditionCoding?.()?.display}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {`${
                    conditions?.[0]?.recordedDate
                      ? `Recorded on ${shortFormat(new Date(conditions?.[0]?.recordedDate ?? ''))}`
                      : ''
                  }
                    ${
                      conditions?.[0]?.recorderResource?.display
                        ? `by ${conditions?.[0]?.recorderResource?.display}`
                        : ''
                    }
                  `}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </EditableCard>
      </Grid>
      <Grid item xs={3} sx={{ display: 'table-row' }}>
        <ItemBox sx={{ borderRadius: 2 }}>
          <Typography
            sx={{
              fontSize: '10px',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontWeight: 500,
              letterSpacing: '0.08em',
            }}
            variant="caption"
          >
            {i18n('episodeOfCare.details.general.careManager', 'crs')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
            }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.primary.main + '30',
                display: 'flex',
              }}
            >
              <Person color="primary" fontSize="large" />
            </Box>

            <Box>
              <Typography fontWeight="bold">{careManager?.display}</Typography>

              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {careManager?.role}
              </Typography>
            </Box>
          </Box>
          <Button
            color="primary"
            variant="contained"
            disabled={!careManager}
            onClick={() => {
              window.open(`${CRS_PATH.provider.root}/${careManager?.id || ''}`, '_blank');
            }}
          >
            {i18n('episodeOfCare.details.general.viewCareManagerProfile', 'crs')}
          </Button>
        </ItemBox>
        <CareTeam eoc={resource} patient={subject} i18n={i18n} handle={handle} />
      </Grid>
      <Grid item xs={12}>
        <EditableCard
          title={i18n('episodeOfCare.details.general.identifiers', 'crs')}
          showEditButton={false}
        >
          <IdentifierDetails
            identifiers={resource?.identifier ?? []}
            catalog={eocIdentifierTypesVS?.asListAll?.() ?? []}
            systemVS={eocIdentifierTypesVS?.getConceptSystem()}
            onUpdateIdentifiers={(data) =>
              handle(
                produce(resource!, (draft) => {
                  draft.identifier = data;
                })
              )
            }
            permissions={{
              isAllowedToAdd: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.ADD],
              }),
              isAllowedToEdit: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.EDIT],
              }),
              isAllowedToDelete: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.DELETE],
              }),
              isAllowedToViewHidden: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.HIDDEN.VIEW],
              }),
              isAllowedToEditHidden: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.HIDDEN.EDIT],
              }),
              isAllowedToDeleteHidden: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.HIDDEN.DELETE],
              }),
              isAllowedToEditExternal: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.EXTERNAL.EDIT],
              }),
              isAllowedToDeleteExternal: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.IDENTIFIER_DETAILS.EXTERNAL.DELETE],
              }),
            }}
          />
        </EditableCard>
      </Grid>
    </Grid>
  );
}
